import { LessonVideo, UserLessonVideoInteraction } from '@/types';

/**
 * Get user progress for a specific video
 */
export function getUserProgressForVideo(
  videoId: number,
  userVideos: UserLessonVideoInteraction[]
): UserLessonVideoInteraction | null {
  return userVideos.find(uv => uv.lesson_video_id === videoId) || null;
}

function watchedSecondsForVideo(
  video: Pick<LessonVideo, 'duration'>,
  uv: UserLessonVideoInteraction | null | undefined,
): number {
  const d = video.duration;
  if (!d || d <= 0) return 0;
  if (uv?.is_completed) return d;
  const left = uv?.left_off_at_seconds;
  if (left == null || left <= 0) return 0;
  return Math.min(left, d);
}

/** 0–100 for sidebar circular icon (incomplete: left_off / duration; completed: 100 — UI shows checkmark instead). */
export function sidebarVideoCirclePercent(
  video: Pick<LessonVideo, 'duration'>,
  uv: UserLessonVideoInteraction | null | undefined,
): number {
  const d = video.duration;
  if (!d || d <= 0) return 0;
  if (uv?.is_completed) return 100;
  const left = uv?.left_off_at_seconds;
  if (left == null || left <= 0) return 0;
  return Math.min(100, Math.round((Math.min(left, d) / d) * 100));
}

/** Lesson row progress bar: weighted watch time / total duration, else RPC fraction 0–1. */
export function lessonProgressBarPercentFromVideos(
  videos: LessonVideo[],
  userVideos: UserLessonVideoInteraction[],
  rpcProgressFraction: number | null | undefined,
): number {
  if (videos.length > 0 && userVideos.length > 0) {
    let totalDuration = 0;
    let totalWatched = 0;
    for (const v of videos) {
      if (!v.duration) continue;
      totalDuration += v.duration;
      totalWatched += watchedSecondsForVideo(v, getUserProgressForVideo(v.id, userVideos));
    }
    if (totalDuration > 0) {
      return Math.max(0, Math.min(100, Math.round((totalWatched / totalDuration) * 100)));
    }
  }
  if (typeof rpcProgressFraction === 'number') {
    return Math.max(0, Math.min(100, Math.round(rpcProgressFraction * 100)));
  }
  return 0;
}

/**
 * Check if all videos are completed
 */
export function areAllVideosCompleted(
  videos: LessonVideo[],
  userVideos: UserLessonVideoInteraction[]
): boolean {
  if (!videos.length) return false;

  return videos.every(video => {
    const userProgress = getUserProgressForVideo(video.id, userVideos);
    return userProgress?.is_completed || false;
  });
}

